import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type {
  BillingAccountDetailView,
  BillingAssignmentView,
  BillingAthleteMatchView,
  SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { Link } from 'react-router-dom';
import { apiRequest } from './api';
import { formatCalendarDate } from './athletes';
import {
  canReverseCharge,
  canAdjustCharge,
  canAllocateCredit,
  canReversePayment,
  canRefundPayment,
  chargeKindKeys,
  chargeStanding,
  chargeStandingKeys,
  formatMoney,
  paymentMethodKeys,
} from './billing';
import { AllocateCreditForm } from './AllocateCreditForm';
import { BillingAccessSection } from './BillingAccessSection';
import { BillingFeeAssignments } from './BillingFeeAssignments';
import { BillingStatement } from './BillingStatement';
import { AnnualDuesPanel } from './AnnualDuesPanel';
import {
  ChargeForm,
  LedgerActionForm,
  PaymentForm,
  errorKeyOf,
  type ErrorKey,
  type LedgerAction,
} from './BillingForms';
import { RemoteSelect, type RemoteOption } from './RemoteSelect';
import { TaskSection } from './TaskSection';

export interface BillingAccountViewProps {
  locale: SupportedLocale;
  organizationId: string;
  accountId: string;
  csrfToken: string;
}

interface OpenAction {
  entryId: string;
  action: LedgerAction | 'allocate';
}

const todayInUtc = (): string => new Date().toISOString().slice(0, 10);

function PositionFigure({
  label,
  amountMinor,
  currency,
  locale,
}: {
  label: string;
  amountMinor: string;
  currency: string;
  locale: SupportedLocale;
}): React.JSX.Element {
  return (
    <Paper variant="outlined" sx={{ padding: 1.5, minWidth: 160, flex: '1 1 160px' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" component="p">
        {formatMoney(amountMinor, currency, locale)}
      </Typography>
    </Paper>
  );
}

export function BillingAccountView({
  locale,
  organizationId,
  accountId,
  csrfToken,
}: BillingAccountViewProps): React.JSX.Element {
  const base = `/api/v1/organizations/${organizationId}/billing`;
  const [account, setAccount] = useState<BillingAccountDetailView | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [notice, setNotice] = useState(false);
  const [openAction, setOpenAction] = useState<OpenAction | null>(null);
  const [athleteId, setAthleteId] = useState('');
  const [startsOn, setStartsOn] = useState(todayInUtc);
  const [assignKey, setAssignKey] = useState(0);

  const load = useCallback(async () => {
    try {
      setAccount(await apiRequest<BillingAccountDetailView>(`${base}/accounts/${accountId}`));
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [accountId, base]);

  useEffect(() => {
    setAccount(null);
    void load();
  }, [load]);

  const findAthletes = useCallback(
    async (text: string): Promise<RemoteOption[]> => {
      if (text.length < 2) return [];
      const matches = await apiRequest<BillingAthleteMatchView[]>(
        `${base}/athletes?search=${encodeURIComponent(text)}`,
      );
      return matches.map((match) => ({
        id: match.id,
        label: `${match.givenName} ${match.familyName} (${String(match.birthYear)})`,
      }));
    },
    [base],
  );

  /** Shows a failure or clears it; a new action also hides the earlier confirmation. */
  const report = useCallback((key: ErrorKey | null) => {
    setErrorKey(key);
    if (key !== null) setNotice(false);
  }, []);

  const finishPosting = useCallback(async () => {
    setOpenAction(null);
    setNotice(true);
    await load();
  }, [load]);

  async function assign(): Promise<void> {
    report(null);
    try {
      await apiRequest(`${base}/accounts/${accountId}/assignments`, {
        method: 'POST',
        body: { athleteId, startsOn },
        csrfToken,
      });
      setAthleteId('');
      setAssignKey((current) => current + 1);
      await finishPosting();
    } catch (error) {
      report(errorKeyOf(error));
    }
  }

  async function endAssignment(assignment: BillingAssignmentView): Promise<void> {
    report(null);
    try {
      await apiRequest(`${base}/accounts/${accountId}/assignments/${assignment.id}/end`, {
        method: 'POST',
        body: {},
        csrfToken,
        ifMatchVersion: assignment.version,
      });
      await finishPosting();
    } catch (error) {
      report(errorKeyOf(error));
    }
  }

  function actionForm(
    entryId: string,
    action: LedgerAction,
    path: string,
    currency: string,
    suggestedAmountMinor?: string,
  ): React.JSX.Element | null {
    if (openAction?.entryId !== entryId || openAction.action !== action) return null;
    return (
      <LedgerActionForm
        locale={locale}
        action={action}
        path={path}
        currency={currency}
        suggestedAmountMinor={suggestedAmountMinor}
        csrfToken={csrfToken}
        onDone={finishPosting}
        onCancel={() => {
          setOpenAction(null);
        }}
        onError={report}
      />
    );
  }

  if (account === null) {
    return (
      <Box>
        <Button component={Link} to="/billing">
          {translate(locale, 'billing.back')}
        </Button>
        {errorKey === null ? (
          <Typography>{translate(locale, 'billing.detail.loading')}</Typography>
        ) : (
          <Alert severity="error">{translate(locale, errorKey)}</Alert>
        )}
      </Box>
    );
  }

  const active = account.status === 'active';
  const { currency } = account;
  const openChargeAction = (chargeId: string, action: LedgerAction): void => {
    setOpenAction({ entryId: chargeId, action });
  };

  return (
    <Box component="section" aria-labelledby="account-title">
      <Button component={Link} to="/billing" sx={{ mb: 1 }}>
        {translate(locale, 'billing.back')}
      </Button>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" component="h2" id="account-title">
          {account.label}
        </Typography>
        <Chip size="small" label={currency} />
        {!active && <Chip size="small" label={translate(locale, 'billing.accounts.closed')} />}
      </Stack>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {notice && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => {
            setNotice(false);
          }}
        >
          {translate(locale, 'billing.posted')}
        </Alert>
      )}
      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <PositionFigure
          locale={locale}
          currency={currency}
          label={translate(locale, 'billing.position.balance')}
          amountMinor={account.position.balanceMinor}
        />
        <PositionFigure
          locale={locale}
          currency={currency}
          label={translate(locale, 'billing.position.overdue')}
          amountMinor={account.position.overdueMinor}
        />
        <PositionFigure
          locale={locale}
          currency={currency}
          label={translate(locale, 'billing.position.upcoming')}
          amountMinor={account.position.upcomingMinor}
        />
        <PositionFigure
          locale={locale}
          currency={currency}
          label={translate(locale, 'billing.position.credit')}
          amountMinor={account.position.unallocatedCreditMinor}
        />
      </Stack>

      <AnnualDuesPanel
        key={account.id}
        locale={locale}
        base={base}
        account={account}
        csrfToken={csrfToken}
        onPosted={finishPosting}
      />

      <Box component="section" aria-labelledby="assignments-title" sx={{ mb: 3 }}>
        <Typography variant="h6" component="h3" id="assignments-title" gutterBottom>
          {translate(locale, 'billing.assignments.title')}
        </Typography>
        {account.assignments.length === 0 && (
          <Typography>{translate(locale, 'billing.assignments.empty')}</Typography>
        )}
        <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {account.assignments.map((assignment) => (
            <Box
              component="li"
              key={assignment.id}
              sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}
            >
              <Typography component="span">
                {assignment.athleteName} · {formatCalendarDate(assignment.startsOn, locale)}
                {assignment.endsOn !== null &&
                  ` ${translate(locale, 'billing.assignments.until')} ${formatCalendarDate(assignment.endsOn, locale)}`}
              </Typography>
              {assignment.endsOn === null && active && (
                <Button
                  size="small"
                  onClick={() => {
                    void endAssignment(assignment);
                  }}
                >
                  {translate(locale, 'billing.assignments.end')}
                </Button>
              )}
            </Box>
          ))}
        </Stack>
        {active && (
          <Stack
            component="form"
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mt: 2 }}
            onSubmit={(event) => {
              event.preventDefault();
              if (athleteId !== '') void assign();
            }}
          >
            <RemoteSelect
              key={`assign-${String(assignKey)}`}
              label={translate(locale, 'billing.assignments.athleteSearch')}
              noMatchesText={translate(locale, 'billing.assignments.noMatches')}
              load={findAthletes}
              onSelect={setAthleteId}
            />
            <TextField
              required
              size="small"
              type="date"
              label={translate(locale, 'billing.col.date')}
              value={startsOn}
              onChange={(event) => {
                setStartsOn(event.target.value);
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <Button type="submit" variant="outlined" disabled={athleteId === '' || startsOn === ''}>
              {translate(locale, 'billing.assignments.assign')}
            </Button>
          </Stack>
        )}
      </Box>

      <BillingAccessSection
        locale={locale}
        base={base}
        accountId={account.id}
        active={active}
        access={account.access}
        csrfToken={csrfToken}
        onChanged={finishPosting}
        onError={report}
      />

      <BillingFeeAssignments
        locale={locale}
        base={base}
        csrfToken={csrfToken}
        account={{ id: account.id, currency, active }}
        athletes={account.assignments}
      />

      {active && (
        <>
          <TaskSection locale={locale} titleKey="billing.charges.post.title">
            <ChargeForm
              locale={locale}
              base={base}
              csrfToken={csrfToken}
              account={account}
              onPosted={finishPosting}
              onError={report}
            />
          </TaskSection>
          <TaskSection locale={locale} titleKey="billing.payment.post.title">
            <PaymentForm
              locale={locale}
              base={base}
              csrfToken={csrfToken}
              account={account}
              charges={account.charges}
              onPosted={finishPosting}
              onError={report}
            />
          </TaskSection>
        </>
      )}

      <Box component="section" aria-labelledby="charges-title" sx={{ mb: 3 }}>
        <Typography variant="h6" component="h3" id="charges-title" gutterBottom>
          {translate(locale, 'billing.charges.title')}
        </Typography>
        {account.charges.length === 0 ? (
          <Typography>{translate(locale, 'billing.charges.empty')}</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label={translate(locale, 'billing.charges.title')}>
              <TableHead>
                <TableRow>
                  <TableCell>{translate(locale, 'billing.col.description')}</TableCell>
                  <TableCell>{translate(locale, 'billing.col.dueOn')}</TableCell>
                  <TableCell align="right">{translate(locale, 'billing.col.amount')}</TableCell>
                  <TableCell align="right">
                    {translate(locale, 'billing.col.outstanding')}
                  </TableCell>
                  <TableCell>{translate(locale, 'billing.col.status')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {account.charges.map((charge) => (
                  <TableRow key={charge.id}>
                    <TableCell>
                      {charge.description}
                      {charge.kind !== 'manual' && (
                        <Typography variant="caption" component="span" color="text.secondary">
                          {' · '}
                          {translate(locale, chargeKindKeys[charge.kind])}
                        </Typography>
                      )}
                      {actionForm(
                        charge.id,
                        'creditNote',
                        `${base}/accounts/${account.id}/charges/${charge.id}/credit-notes`,
                        currency,
                        charge.outstandingMinor,
                      )}
                      {actionForm(
                        charge.id,
                        'reverseCharge',
                        `${base}/accounts/${account.id}/charges/${charge.id}/reverse`,
                        currency,
                      )}
                    </TableCell>
                    <TableCell>{formatCalendarDate(charge.dueOn, locale)}</TableCell>
                    <TableCell align="right">
                      {formatMoney(charge.amountMinor, currency, locale)}
                    </TableCell>
                    <TableCell align="right">
                      {formatMoney(charge.outstandingMinor, currency, locale)}
                    </TableCell>
                    <TableCell>
                      {translate(locale, chargeStandingKeys[chargeStanding(charge)])}
                    </TableCell>
                    <TableCell>
                      {active && canAdjustCharge(charge) && (
                        <Button
                          size="small"
                          onClick={() => {
                            openChargeAction(charge.id, 'creditNote');
                          }}
                        >
                          {translate(locale, 'billing.charge.creditNote')}
                        </Button>
                      )}
                      {active && canReverseCharge(charge) && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            openChargeAction(charge.id, 'reverseCharge');
                          }}
                        >
                          {translate(locale, 'billing.charge.reverse')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>

      <Box component="section" aria-labelledby="payments-title">
        <Typography variant="h6" component="h3" id="payments-title" gutterBottom>
          {translate(locale, 'billing.payments.title')}
        </Typography>
        {account.payments.length === 0 ? (
          <Typography>{translate(locale, 'billing.payments.empty')}</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label={translate(locale, 'billing.payments.title')}>
              <TableHead>
                <TableRow>
                  <TableCell>{translate(locale, 'billing.col.date')}</TableCell>
                  <TableCell>{translate(locale, 'billing.col.method')}</TableCell>
                  <TableCell>{translate(locale, 'billing.payment.payerReference')}</TableCell>
                  <TableCell>{translate(locale, 'billing.col.reference')}</TableCell>
                  <TableCell align="right">{translate(locale, 'billing.col.amount')}</TableCell>
                  <TableCell align="right">{translate(locale, 'billing.col.available')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {account.payments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>{formatCalendarDate(payment.paidOn, locale)}</TableCell>
                    <TableCell>{translate(locale, paymentMethodKeys[payment.method])}</TableCell>
                    <TableCell>{payment.payerReference ?? account.label}</TableCell>
                    <TableCell>
                      {payment.reference}
                      {payment.reversed && (
                        <Chip
                          size="small"
                          sx={{ ml: 1 }}
                          label={translate(locale, 'billing.payment.reversed')}
                        />
                      )}
                      {BigInt(payment.refundedMinor) > 0n && (
                        <Chip
                          size="small"
                          sx={{ ml: 1 }}
                          label={`${translate(locale, 'billing.payment.refunded')}: ${formatMoney(payment.refundedMinor, currency, locale)}`}
                        />
                      )}
                      {openAction?.entryId === payment.id && openAction.action === 'allocate' && (
                        <AllocateCreditForm
                          locale={locale}
                          base={base}
                          accountId={account.id}
                          paymentId={payment.id}
                          availableMinor={payment.availableMinor}
                          currency={currency}
                          charges={account.charges}
                          csrfToken={csrfToken}
                          onDone={finishPosting}
                          onCancel={() => {
                            setOpenAction(null);
                          }}
                          onError={report}
                        />
                      )}
                      {actionForm(
                        payment.id,
                        'reversePayment',
                        `${base}/payments/${payment.id}/reverse`,
                        currency,
                      )}
                      {actionForm(
                        payment.id,
                        'refund',
                        `${base}/payments/${payment.id}/refunds`,
                        currency,
                        payment.availableMinor,
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {formatMoney(payment.amountMinor, currency, locale)}
                    </TableCell>
                    <TableCell align="right">
                      {formatMoney(payment.availableMinor, currency, locale)}
                    </TableCell>
                    <TableCell>
                      {active && canAllocateCredit(payment, account.charges) && (
                        <Button
                          size="small"
                          onClick={() => {
                            setOpenAction({ entryId: payment.id, action: 'allocate' });
                          }}
                        >
                          {translate(locale, 'billing.payment.allocate')}
                        </Button>
                      )}
                      {active && canRefundPayment(payment) && (
                        <Button
                          size="small"
                          onClick={() => {
                            setOpenAction({ entryId: payment.id, action: 'refund' });
                          }}
                        >
                          {translate(locale, 'billing.payment.refund')}
                        </Button>
                      )}
                      {active && canReversePayment(payment) && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            setOpenAction({ entryId: payment.id, action: 'reversePayment' });
                          }}
                        >
                          {translate(locale, 'billing.payment.reverse')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>

      <BillingStatement locale={locale} base={base} accountId={account.id} refreshToken={account} />
    </Box>
  );
}
